<template>
  <div>
    <v-card class="ma-4">
      <v-card-text class="pa-0">
        <base-data-grid
            :ref="grid.key"
            :gridKey="grid.key"
            :dataSource="grid.dataSource"
            :columns="grid.columns"
            @on-add-click="add"
            :exportFileName="title"
            :isStateStoringEnabled="true"
            :allowGrouping="false"
            :allowFilterPanel="false"
            :title="title"
        >
          <template slot="customActionTemplates" slot-scope="row">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                    v-on="on"
                    @click.prevent="editItem(row.data.item)"
                    class="px-2"
                >
                  <v-icon color="primary">mdi-pencil</v-icon>
                </a>
              </template>
              <span>{{ $t("Edit") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                    v-on="on"
                    @click.prevent="openResultGroupSettings(row.data.item)"
                    class="px-2"
                >
                  <v-icon color="primary">mdi-file-tree-outline</v-icon>
                </a>
              </template>
              <span>
                {{ $t("EditDefaultGroupForBasket") }}
              </span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                    v-on="on"
                    @click.prevent="activate(row.data.item)"
                    class="px-2"
                >
                  <v-icon v-if="!row.data.item.IsActive" color="red"
                  >mdi-folder-lock</v-icon
                  >
                  <v-icon v-if="row.data.item.IsActive" color="green"
                  >mdi-folder-lock-open</v-icon
                  >
                </a>
              </template>
              <span>
                {{ row.data.item.IsActive ? $t("Deactivate") : $t("Activate") }}
              </span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <a
                    v-on="on"
                    @click.prevent="deleteItem(row.data.item)"
                    class="px-2"
                >
                  <v-icon color="red">mdi-delete</v-icon>
                </a>
              </template>
              <span>{{ $t("Delete") }}</span>
            </v-tooltip>
          </template>
        </base-data-grid>
      </v-card-text>
    </v-card>
    <base-modal
        :show="showGroup"
        :title="$t(&quot;Group&quot;)"
        @on-confirm="group()"
        @on-cancel="hideGroup()"
        :okText="$t('Save')"
        :closeText="$t('Close')"
        maxWidth="80%"
    >
      <GroupModal :ref="groupSettingsModalKey" :field-list="fieldList" @on-save="saveBasketGroupSettings" />
    </base-modal>
  </div>
</template>

<script>
import BasketService from '@/services/BasketService';
import Utils from '@/ecm/common/Utils';
import GroupModal from '@/components/search/results/GroupModal.vue';
import ContentTypeService from '@/services/ContentTypeService';
import FieldType from '@/ecm/variables/FieldType';
import StandardFieldCode from '@/ecm/variables/StandardFieldCode';
import Basket from './Basket.vue';

export default {
    components: {
        GroupModal,
        Basket
    },
    data() {
        return {
            title: this.$t('Baskets'),
            childComponents: ['Basket'],
            grid: {
                key: 'basketGrid',
                dataSource: BasketService.getGrid(),
                columns: [
                    {
                        field: 'Name',
                        caption: this.$t('Name'),
                        type: 'string',
                    },
                    {
                        field: 'RoleTypesNames',
                        caption: this.$t('Roles'),
                        type: 'string',
                    },
                ],
            },
            modalKey: 'modalBasket',
            groupSettingsModalKey: 'groupSettingsModalKey',
            fieldList: [],
            showGroup: false,
        };
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.childComponents = [Basket.name];
            if (!vm.childComponents.includes(from.name)) {
                vm.gridInstance.saveState(null);
            }
        });
    },
    beforeRouteLeave(to, from, next) {
        this.$nextTick(() => {
            if (this.childComponents.includes(to.name)) {
                const gridState = this.gridInstance.loadState();
                this.gridInstance.saveState(gridState);
            }
            next();
        });
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modalKey];
        },
        groupSettingsModalInstance() {
            return this.$refs[this.groupSettingsModalKey];
        },
        gridInstance() {
            return this.$refs[this.grid.key];
        },
    },
    methods: {
        editItem(dto) {
            this.$router.push(`/administration/baskets/${dto.Id}`);
        },
        openResultGroupSettings(dto) {
            var standardFields = this.$store.getters['account/searchStandardFields'];
            this.fieldList = [];
            standardFields.forEach((sf) => {
                if (sf.Code !== StandardFieldCode.Fulltext) {
                    sf.Title = sf.DisplayText;
                    sf.Type = FieldType[sf.Type];
                    sf.IsVisible = true;
                    this.fieldList.push(sf);
                }
            });

            ContentTypeService.getFields(dto.ContentTypeIDs.toString()).then(
                (result) => {
                    result.forEach((t) => {
                        t.Title = t.Name;
                        t.Type = FieldType[t.Type];
                        this.fieldList.push(t);
                    });
                    this.showGroup = true;

                    // Wait for the next tick to ensure the components are rendered and $refs are populated
                    this.$nextTick(() => {
                        this.groupSettingsModalInstance.load(null, dto.Id);
                    });
                }
            );
        },
        activate(dto) {
            BasketService.activate(dto.Id).then((result) => {
                this.gridInstance.refresh();
            });
        },
        deleteItem(dto) {
            this.$confirm(this.$t('ItemDeleteConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-alert',
                color: 'red',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    BasketService.delete(dto.Id).then((result) => {
                        if (result) {
                            this.gridInstance.refresh();
                            Utils.toast.success(
                                this.$t('Basket') + ' ' + this.$t('DeletedSuccessfullyFem')
                            );
                        }
                    });
                }
            });
        },
        add() {
            this.$router.push('/administration/baskets/new');
        },
        saveBasketGroupSettings(dto) {
            const request = {
                BasketId: dto.BasketId,
                BasketGroupSettingsDTO: dto.GroupSettings
            };

            BasketService.saveGroupSettingsForBasket(request).then((result) => {
                if (result) {
                    this.gridInstance.refresh();
                    Utils.toast.success(
                        this.$t('Basket') + ' ' + this.$t('SavedSuccessfully')
                    );
                }
            });
        },
        hideGroup() {
            this.groupSettingsModalInstance.reset();
            this.showGroup = false;
        },
        group() {
            this.groupSettingsModalInstance.onAccept();
            this.hideGroup();
        },
        onGrouped(groupItems) {
            this.groupItems = groupItems;
        },
    },
};
</script>